import { useLayoutEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * Vendor (ApartmentSync / Repli360 / RRAC) appends modal shells, date pickers,
 * and loader spinners directly to <body>. Those nodes sit outside React, so
 * SPA navigations (Floor Plans → Interactive Map) and StrictMode remounts can
 * leave stacked duplicates — unstyled "Lease Now" / "Select Desired Move In
 * Date" / loading-dot rows over the header.
 *
 * Mirrors the cleanup selectors in `@vibesites/blocks` useRRACScript, and
 * also drops duplicate loader nodes the hook does not cover.
 */
const RRAC_BODY_HELPER_SELECTORS = [
  '.rrac-popup',
  '.rrac_modal_container',
  '.rrac_loader_container',
] as const;

function dedupeRRACBodyHelpers(): void {
  if (typeof document === 'undefined') return;

  for (const sel of RRAC_BODY_HELPER_SELECTORS) {
    const nodes = Array.from(document.querySelectorAll(sel)).filter(
      (el) => el.parentElement === document.body,
    );
    // Keep the newest shell; remove older siblings left by a prior mount.
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i]!.remove();
    }
  }
}

// ============================================================================
// Component
// ============================================================================

export function InteractiveMapSection() {
  const [mapArmed, setMapArmed] = useState(false);
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'apartment-sync-map',
  });

  useLayoutEffect(() => {
    dedupeRRACBodyHelpers();

    const observer = new MutationObserver(() => {
      dedupeRRACBodyHelpers();
    });
    observer.observe(document.body, { childList: true });

    return () => {
      observer.disconnect();
      // Drop leftovers when leaving the page so the next route starts clean.
      for (const sel of RRAC_BODY_HELPER_SELECTORS) {
        document.querySelectorAll(sel).forEach((el) => {
          if (el.parentElement === document.body) el.remove();
        });
      }
    };
  }, []);

  return (
    <div
      className={`relative min-h-[400px] md:min-h-[500px] flex flex-col bg-[var(--vibe-color-background)]${mapArmed ? '' : ' map-scroll-block'}`}
      onMouseLeave={() => setMapArmed(false)}
    >
      <EditableToken path="interactivemapsection.map_block" as="div" className="flex flex-col flex-1">
        {mapSlot ? (
          <SectionContextProvider sectionId={mapSlot.sectionId}>
            <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div>No block selected</div>
        )}
      </EditableToken>
      {!mapArmed && (
        <button
          type="button"
          className="absolute inset-0 z-10 cursor-pointer bg-transparent"
          aria-label="Click to interact with the map"
          onClick={() => setMapArmed(true)}
        />
      )}
      <style>{`
        .map-scroll-block iframe { pointer-events: none !important; }
      `}</style>
    </div>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapSection;
